<mat-sidenav-container class="insurance-list-container">
  <mat-sidenav mode="side" opened class="insurance-sidenav">
    <div class="sidenav-header">
      <button mat-raised-button color="primary" class="add-insurance-btn" (click)="addInsurance()">
        <mat-icon>add</mat-icon>
        Add Insurance
      </button>
    </div>

    @if (loading()) {
      <div class="loading-spinner">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    } @else {
      @if (dataSource().length === 0) {
        <div class="no-data">
          No insurance found. Click "Add Insurance" to create one.
        </div>
      } @else {
        <mat-nav-list>
          @for (insurance of dataSource(); track insurance.id) {
            <a mat-list-item 
               [class.selected]="selectedInsurance()?.id === insurance.id"
               (click)="selectInsurance(insurance)">
              <mat-icon matListItemIcon>policy</mat-icon>
              <span matListItemTitle>{{getName(insurance.contact_id)}}</span>
              <span matListItemLine>{{ num_loans(insurance.insurance_loans_collection) }} | {{ num_riders(insurance.riders_collection) }}</span>
            </a>
          }
        </mat-nav-list>
      }
    }
  </mat-sidenav>

  <mat-sidenav-content>
    @if (selectedInsurance()) {
      <div class="loans-container">
        <div class="controls-row">
          <div class="button-group">
            <button mat-raised-button color="primary" class="add-rider-btn" (click)="addRider()">
              <mat-icon>add</mat-icon>
              Add Rider
            </button>
            <button mat-raised-button color="primary" class="add-loan-btn" (click)="addInsuranceLoan()">
              <mat-icon>add</mat-icon>
              Add Loan
            </button>
            <button mat-raised-button color="accent" (click)="editInsurance(selectedInsurance())">
              <mat-icon>edit</mat-icon>
              Edit Insurance
            </button>
            <button mat-raised-button color="primary" class="premium-payment-btn" (click)="openPremiumPaymentDialog()">
              <mat-icon>payment</mat-icon>
              Premium Payment
            </button>
            <button mat-raised-button color="primary" class="loan-payment-btn" (click)="openLoanPaymentDialog()">
              <mat-icon>payment</mat-icon>
              Loan Payment
            </button>
          </div>
        </div>

        <!-- Insurance Policy Summary Section -->
        <div class="section-header">
          <h2>Policy Summary</h2>
        </div>
        
        <div class="policy-summary-container">
          <div class="policy-summary-card">
            <div class="policy-summary-grid">
              <!-- Column 1: Insurance Amount -->
              <div class="policy-summary-column">
                <div class="column-header">Insurance Amount</div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Base:</div>
                  <div class="policy-summary-value">{{selectedInsurance()?.face_value | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Riders:</div>
                  <div class="policy-summary-value">{{calculateTotalRiderInsurance(selectedInsurance()?.riders_collection || []) | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-divider"></div>
                <div class="policy-summary-row total">
                  <div class="policy-summary-label">Total:</div>
                  <div class="policy-summary-value">{{totalInsuranceAmount() | currency:'USD':true:'1.2-2'}}</div>
                </div>
              </div>
              
              <!-- Column 2: Premium -->
              <div class="policy-summary-column">
                <div class="column-header">Premium</div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Base:</div>
                  <div class="policy-summary-value">{{selectedInsurance()?.premium | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Riders:</div>
                  <div class="policy-summary-value">{{calculateTotalRiderPremium(selectedInsurance()?.riders_collection || []) | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-divider"></div>
                <div class="policy-summary-row total">
                  <div class="policy-summary-label">Total:</div>
                  <div class="policy-summary-value">{{totalPremiumAmount() | currency:'USD':true:'1.2-2'}}</div>
                </div>
              </div>
              
              <!-- Column 3: Cash Value -->
              <div class="policy-summary-column">
                <div class="column-header">Cash Value</div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Base:</div>
                  <div class="policy-summary-value">{{selectedInsurance()?.cash_value | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Riders:</div>
                  <div class="policy-summary-value">{{calculateTotalRiderCashValue(selectedInsurance()?.riders_collection || []) | currency:'USD':true:'1.2-2'}}</div>
                </div>
                <div class="policy-summary-divider"></div>
                <div class="policy-summary-row total">
                  <div class="policy-summary-label">Total:</div>
                  <div class="policy-summary-value">{{totalCashValue() | currency:'USD':true:'1.2-2'}}</div>
                </div>
              </div>
              
              <!-- Column 4: Paid To Date -->
              <div class="policy-summary-column">
                <div class="column-header">Details</div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Paid To Date:</div>
                  <div class="policy-summary-value">{{selectedInsurance()?.paid_to_date | date:'MMM d, yyyy'}}</div>
                </div>
                <div class="policy-summary-row">
                  <div class="policy-summary-label">Number of Riders:</div>
                  <div class="policy-summary-value">{{selectedInsurance()?.riders_collection?.length || 0}}</div>
                </div>
                <div class="policy-summary-divider"></div>
                <div class="policy-summary-row total">
                  <div class="policy-summary-label">Status:</div>
                  <div class="policy-summary-value" [ngClass]="{'text-success': isPolicyPaidUpToDate(), 'text-danger': !isPolicyPaidUpToDate()}">
                    @if(isPolicyPaidUpToDate()) {
                      <span>Current</span>
                    } @else {
                      <span>Payment Due</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Riders Section -->
        <div class="section-header">
          <h2>Riders</h2>
        </div>

        @if (selectedInsurance()?.riders_collection?.length === 0) {
          <div class="no-data">
            <p>No riders found. Click "Add Rider" to create one.</p>
          </div>
        } @else {
          <div class="table-container">
            <table mat-table [dataSource]="selectedInsurance()?.riders_collection || []" matSort class="full-width">
              <!-- Name Column -->
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
                <td mat-cell *matCellDef="let rider">{{rider.name}}</td>
              </ng-container>

              <!-- Insurance Column -->
              <ng-container matColumnDef="riderInsurance">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Insurance</th>
                <td mat-cell *matCellDef="let rider">{{rider.face_value | currency:'USD':true:'1.2-2'}}</td>
              </ng-container>

              <!-- Premium Column -->
              <ng-container matColumnDef="riderPremium">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Premium</th>
                <td mat-cell *matCellDef="let rider">{{rider.premium | currency:'USD':true:'1.2-2'}}</td>
              </ng-container>

              <!-- Cash Value Column -->
              <ng-container matColumnDef="cashValue">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Cash Value</th>
                <td mat-cell *matCellDef="let rider">{{rider.cash_value | currency:'USD':true:'1.2-2'}}</td>
              </ng-container>

              <!-- Issue Date Column -->
              <ng-container matColumnDef="issueDate">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Issue Date</th>
                <td mat-cell *matCellDef="let rider">{{rider.issue_date | date}}</td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>Actions</th>
                <td mat-cell *matCellDef="let rider">
                  <button mat-icon-button [matMenuTriggerFor]="riderMenu">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #riderMenu="matMenu">
                    <button mat-menu-item (click)="editRider(rider)">
                      <mat-icon>edit</mat-icon>
                      <span>Edit</span>
                    </button>
                    <button mat-menu-item (click)="deleteRider(rider)">
                      <mat-icon>delete</mat-icon>
                      <span>Delete</span>
                    </button>
                  </mat-menu>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="riderColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: riderColumns;"></tr>
            </table>
          </div>
        }
        @if (selectedInsurance()?.insurance_loans_collection?.length == 0) {
          <!-- Loans Section -->
          <div class="section-header">
            <h2>Loans</h2>
          </div>
          <div class="no-data">
            <p>No loans found. Click "Add Loan" to create one.</p>
          </div>
        } @else {
          @for (st of states; track utilityService.trackByFn($index, st)) {
            @if (getLoansByState(st)?.length) {
              <div class="section-header">
                <h2>{{utilityService.formatState(st)}} Loans</h2>
              </div>
              <div class="table-container">
                <table mat-table [dataSource]="getLoansByState(st)" matSort class="full-width">
                  <!-- Name Column -->
                  <ng-container matColumnDef="name">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
                    <td mat-cell *matCellDef="let loan">{{loan.name }}</td>
                  </ng-container>

                  <!-- Bank Column -->
                  <ng-container matColumnDef="bank">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Bank</th>
                    <td mat-cell *matCellDef="let loan">{{ getBank(loan.bank_id) }}</td>
                  </ng-container>

                  <!-- Amount Column -->
                  <ng-container matColumnDef="amount">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Amount</th>
                    <td mat-cell *matCellDef="let loan">{{loan.amount | currency:'USD':'symbol':'1.2-2' }}</td>
                  </ng-container>

                  <!-- Balance Column -->
                  <ng-container matColumnDef="balance">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Balance</th>
                    <td mat-cell *matCellDef="let loan">{{ loan.current_balance | currency:'USD':'symbol':'1.2-2' }}</td>
                  </ng-container>

                  <!-- Interest Fee Column -->
                  <ng-container matColumnDef="interestFee">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Interest Fee</th>
                    <td mat-cell *matCellDef="let loan">{{loan.interest_fee | currency:'USD':'symbol':'1.2-2' }}</td>
                  </ng-container>

                  <!-- Close Date Column -->
                  <ng-container matColumnDef="closeDate">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header>Close Date</th>
                    <td mat-cell *matCellDef="let loan">{{loan.close_date | date}}</td>
                  </ng-container>

                  <!-- Actions Column -->
                  <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef>Actions</th>
                    <td mat-cell *matCellDef="let loan">
                      <button mat-icon-button [matMenuTriggerFor]="menu">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #menu="matMenu">
                        <button mat-menu-item (click)="editInsuranceLoan(loan)">
                          <mat-icon>edit</mat-icon>
                          <span>Edit</span>
                        </button>
                        <button mat-menu-item (click)="deleteInsuranceLoan(loan)">
                          <mat-icon>delete</mat-icon>
                          <span>Delete</span>
                        </button>
                      </mat-menu>
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
                </table>
              </div>
            }
          }
        }
      </div>
    } @else {
      <div class="no-selection">
        <mat-icon>policy</mat-icon>
        <p>Select an insurance from the sidebar to view its loans</p>
      </div>
    }
  </mat-sidenav-content>
</mat-sidenav-container>
